import React from 'react';
import { Alert, Button, Modal, Nav, Spinner } from 'react-bootstrap';
import {
    BasicInfoForm,
    Unlock,
    BasicInfo,
    UserRoles,
    WelcomeEmail,
    MfaInfo,
    AddedRoleType,
    EntityTypeType,
    UserIntegrations,
} from '~/components/Admin';
import { ServicesContext, useAdminService } from '~/services';
import { enums, formatUserName, useServiceSubscribe } from '~/util';
import { BSModal } from '~/components/Bootstrap';

const basicInfoFormId = 'edit-user-basic-info';
export interface UserRoleType {
    id: number;
    organization?: { id: number; is_enabled?: boolean; tin: string; name: string };
    organization_roles?: string[];
    market?: { id: number; name: string };
    market_roles?: string[];
    macl?: { id: number; name: string };
    macl_roles?: string[];
    markets?: { id: number; name: string }[];
}

interface EditUserModalProps {
    user: UserType;
    show: boolean;
    setShow: (x: boolean) => void;
    refreshUserList: () => void;
}
export function EditUserModal({ user, show, setShow, refreshUserList }: EditUserModalProps) {
    const context = React.useContext(ServicesContext);
    const { getUser, events, getUserRoles } = useAdminService();
    const [loading, setLoading] = React.useState(false);
    const [loadingRoles, setLoadingRoles] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const [serverError, setReceivedServerError] = React.useState<
        null | string | { message: string }[]
    >(null);
    const [retrievedUser, setRetrievedUser] = React.useState<UserType>();
    const [welcomeSent, setWelcomeSent] = React.useState(false);
    const [addedRoles, setAddedRoles] = React.useState<AddedRoleType[]>([]);
    const [tab, setTab] = React.useState('info');
    const [editable, setEditable] = React.useState(true);
    const [userRoles, setUserRoles] = React.useState<UserRoleType[]>([]);

    useServiceSubscribe(events.user_updated, (newUser: UserType) => {
        if (newUser.id === user.id) {
            setEditing(false);
            setRetrievedUser(newUser);
        }
    });

    const { accessEntityName, accessEntityType } = React.useMemo(() => {
        let name = `${enums(['user', 'access_type', Number(retrievedUser?.access_type)])}s`;
        if (name === 'TINs') name = 'Organizations';
        const type = name.slice(0, -1).toLowerCase();
        return { accessEntityName: name, accessEntityType: type };
    }, [retrievedUser?.access_type]);

    const retrieveUser = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await getUser(user.id, {
                handledErrors: { 400: true },
            });
            setRetrievedUser(result);
        } catch (e) {
            setHasError(true);
            setReceivedServerError(e.response);
        } finally {
            setLoading(false);
        }
    }, [user.id, getUser]);

    React.useEffect(() => {
        if (show) {
            retrieveUser();
        }
    }, [show, retrieveUser]);

    // determine whether user is editable for org admins
    React.useEffect(() => {
        if (accessEntityType === 'organization') {
            setEditable(
                userRoles &&
                    userRoles.some((role) => context.user.isAdminInOrg(role.organization?.id))
            );
        } else {
            setEditable(true);
        }
    }, [accessEntityType, context.user, userRoles]);

    // load user roles based on accessEntityType
    React.useEffect(() => {
        (async () => {
            if (show && accessEntityType !== 'undefined') {
                try {
                    setLoadingRoles(true);
                    if (user.id) {
                        const userResult = await getUserRoles(
                            user.id,
                            accessEntityType as EntityTypeType
                        );
                        setUserRoles(userResult);
                    }
                    setLoadingRoles(false);
                } catch (e) {
                    setLoadingRoles(false);
                }
            }
        })();
    }, [user.id, getUserRoles, accessEntityType, show]);

    useServiceSubscribe(
        events[`user_${accessEntityType as EntityTypeType}_added`],
        (roles: UserRoleType[]) => {
            setUserRoles(roles);
        }
    );
    useServiceSubscribe(
        events[`user_${accessEntityType as EntityTypeType}_updated`],
        (updated: UserRoleType) => {
            setUserRoles((prev) =>
                prev.map((x) => {
                    if (x.id === updated.id) return updated;
                    return x;
                })
            );
        }
    );
    useServiceSubscribe(
        events[`user_${accessEntityType as EntityTypeType}_removed`],
        (removedRoleId: number) => {
            setUserRoles((prev) => prev.filter((role) => role.id !== removedRoleId));
        }
    );

    function handleClose() {
        setShow(false);
        setEditing(false);
        setHasError(false);
        setTab('info');
        setWelcomeSent(false);
        setAddedRoles([]);
        // refresh user list if org admin no longer has orgs in common with user
        const orgsWithAdminPrivilege = userRoles.filter((role) =>
            context.user.isAdminInOrg(role.organization?.id)
        );
        if (!context.user.isGlobalAdmin() && orgsWithAdminPrivilege.length === 0) {
            refreshUserList();
        }
    }

    function setServerError(error: { message: string }) {
        setHasError(true);
        setReceivedServerError(error.message);
    }

    return (
        <BSModal
            id="user-info-modal"
            show={show}
            onHide={handleClose}
            aria-labelledby="edit-user-title"
        >
            <Modal.Header closeButton>
                <Modal.Title id="edit-user-title" role="heading" aria-level={1}>
                    {loading ? formatUserName(user) : formatUserName(retrievedUser)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="card-header user-info-tabs">
                    <Nav variant="tabs" className="card-header-tabs">
                        <Nav.Item>
                            <Nav.Link
                                className={tab === 'info' ? 'active' : ''}
                                onClick={() => setTab('info')}
                            >
                                Information
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                className={tab === 'access' ? 'active' : ''}
                                onClick={() => setTab('access')}
                            >
                                Access
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>
                {loading && (
                    <div className="container text-center">
                        <Spinner animation="border" role="status" className="me-1" />
                    </div>
                )}
                {!loading && hasError && (
                    <div>
                        <Alert variant="danger">
                            {Array.isArray(serverError) ? (
                                <ul>
                                    {serverError.map((x) => (
                                        <li key={x.message}>{x.message}</li>
                                    ))}
                                </ul>
                            ) : (
                                serverError
                            )}
                        </Alert>
                    </div>
                )}
                {!loading && retrievedUser?.id && (
                    <div>
                        {tab === 'info' && (
                            <>
                                {retrievedUser && (
                                    <Unlock setServerError={setServerError} user={retrievedUser} />
                                )}
                                {welcomeSent && (
                                    <Alert variant="success">
                                        <div>
                                            {context.user.hasFeatureToggle(
                                                'auth_one_healthcare_id_only'
                                            )
                                                ? 'Activation email sent!'
                                                : 'Welcome email sent!'}
                                        </div>
                                    </Alert>
                                )}

                                <div className="card mb-2">
                                    <div className="card-header">
                                        {editable && !editing && (
                                            <Button
                                                onClick={() => setEditing(true)}
                                                variant="primary"
                                                size="sm"
                                                className="pull-right ms-1"
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        {editable && (
                                            <WelcomeEmail
                                                user={retrievedUser}
                                                setServerError={setServerError}
                                                onCompleted={() => setWelcomeSent(true)}
                                            />
                                        )}
                                        Basic Information
                                    </div>
                                    <div className="card-body">
                                        {!editing || !editable ? (
                                            <BasicInfo
                                                user={retrievedUser}
                                                setServerError={setServerError}
                                                editable={editable}
                                            />
                                        ) : (
                                            <BasicInfoForm
                                                user={{ ...retrievedUser }}
                                                id={basicInfoFormId}
                                                onCancel={() => setEditing(false)}
                                            />
                                        )}
                                    </div>
                                </div>
                                <MfaInfo user={retrievedUser} retrieveUser={retrieveUser} />
                                <UserIntegrations
                                    user={retrievedUser}
                                    editable={editable}
                                    setServerError={setServerError}
                                />
                            </>
                        )}

                        <UserRoles
                            userId={user.id}
                            show={tab === 'access'}
                            entityType={accessEntityType as EntityTypeType}
                            entityLabel={accessEntityName}
                            addedRoles={addedRoles}
                            setAddedRoles={setAddedRoles}
                            userRoles={userRoles}
                            loadingRoles={loadingRoles}
                        />
                    </div>
                )}
            </Modal.Body>
        </BSModal>
    );
}
